import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Heart, MapPin, Truck, Clock, Flag, Tag, CalendarPlus, MessageCircle, Users, Car } from "lucide-react";
import { api, errMsg, imgUrl, money, CATEGORY_LABELS, CONDITION_LABELS, fmtDate, timeAgo, vehicleLine } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, ErrorState, StatusPill, Avatar } from "@/components/States";
import { TrustSummary } from "@/components/TrustSummary";
import { OfferDialog, PickupDialog, QuestionDialog, ReportDialog } from "@/components/DealDialogs";
import { btnPrimary, btnDark, btnOutline, btnGhost, card } from "@/lib/ui";

const Gallery = ({ photos, title }) => {
  const [i, setI] = useState(0);
  return (
    <div>
      <div className="aspect-[4/3] sm:aspect-square lg:aspect-[4/3] bg-slate-100 rounded-2xl overflow-hidden -mx-4 sm:mx-0 rounded-none sm:rounded-2xl">
        {photos[i] && <img data-testid="listing-main-photo" src={imgUrl(photos[i])} alt={title} className="w-full h-full object-cover" />}
      </div>
      {photos.length > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto">
          {photos.map((p, idx) => (
            <button key={p} data-testid={`listing-thumb-${idx}`} onClick={() => setI(idx)} className={`h-16 w-16 rounded-xl overflow-hidden border-2 shrink-0 ${idx === i ? "border-blue-600" : "border-transparent"}`}>
              <img src={imgUrl(p)} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const SellerTools = ({ l, reload }) => {
  const nav = useNavigate();
  const setStatus = async (status) => {
    try {
      await api.post(`/listings/${l.id}/status`, { status });
      toast.success(status === "sold" ? "Marked as sold" : status === "archived" ? "Listing archived" : "Listing relisted");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const reconfirm = async () => {
    try {
      await api.post(`/listings/${l.id}/reconfirm`);
      toast.success("Availability reconfirmed");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div data-testid="seller-tools" className={card + " space-y-3"}>
      <p className="text-sm font-semibold text-slate-900">This is your listing</p>
      {l.min_offer != null && <p className="text-xs text-slate-500">Private minimum offer: {money(l.min_offer)} (only you see this)</p>}
      {l.status === "paused" && <p className="text-sm text-amber-700">Paused — please confirm it's still available.</p>}
      <div className="flex flex-wrap gap-2">
        {l.status === "paused" && <button data-testid="listing-reconfirm-btn" className={btnPrimary} onClick={reconfirm}>Still available</button>}
        {["active", "paused", "reserved"].includes(l.status) && <button data-testid="listing-edit-btn" className={btnOutline} onClick={() => nav(`/listing/${l.id}/edit`)}>Edit</button>}
        {["active", "paused", "reserved"].includes(l.status) && <button data-testid="listing-mark-sold-btn" className={btnDark} onClick={() => setStatus("sold")}>Mark sold</button>}
        {["active", "paused", "reserved"].includes(l.status) && <button data-testid="listing-archive-btn" className={btnGhost} onClick={() => setStatus("archived")}>Archive</button>}
        {l.status === "archived" && <button data-testid="listing-relist-btn" className={btnPrimary} onClick={() => setStatus("active")}>Relist</button>}
      </div>
    </div>
  );
};

const BuyerActions = ({ l, reload }) => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [dlg, setDlg] = useState(null);
  const v = l.viewer;
  const need = (fn) => () => (user ? fn() : nav("/login", { state: { from: `/listing/${l.id}` } }));
  const waitlist = async () => {
    try {
      if (v.on_waitlist) await api.delete(`/listings/${l.id}/waitlist`);
      else await api.post(`/listings/${l.id}/waitlist`);
      toast.success(v.on_waitlist ? "Left the waitlist" : "You're on the waitlist — we'll notify you if it frees up");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const acceptedOffer = v.latest_offer?.status === "accepted" ? v.latest_offer : null;
  return (
    <div data-testid="buyer-actions" className="space-y-2">
      {l.status === "active" && !v.open_appointment_id && (
        <>
          {l.price_type === "offers" && <button data-testid="action-make-offer" className={btnPrimary + " w-full h-12"} onClick={need(() => setDlg("offer"))}><Tag className="h-4 w-4" /> Make an offer</button>}
          <button data-testid="action-schedule-pickup" className={(l.price_type === "firm" ? btnPrimary : btnDark) + " w-full h-12"} onClick={need(() => setDlg("pickup"))}>
            <CalendarPlus className="h-4 w-4" /> Schedule pickup at {money(acceptedOffer?.final_amount ?? l.price)}
          </button>
        </>
      )}
      {v.open_appointment_id && (
        <Link data-testid="action-view-pickup" to={`/inbox/${v.conversation_id}`} className={btnPrimary + " w-full h-12"}>
          {v.reserved_by_me ? "Your pickup is confirmed — view details" : "View your pickup request"}
        </Link>
      )}
      {l.status === "reserved" && !v.reserved_by_me && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 space-y-2">
          <p>Reserved — another buyer has a confirmed pickup. {l.waitlist_count > 0 && `${l.waitlist_count} on the waitlist.`}</p>
          <button data-testid="action-waitlist" className={btnOutline + " w-full"} onClick={need(waitlist)}><Users className="h-4 w-4" /> {v.on_waitlist ? "Leave waitlist" : "Join waitlist"}</button>
        </div>
      )}
      {l.status === "paused" && <p className="text-sm text-slate-600 bg-slate-100 rounded-xl p-3">Paused while the seller reconfirms availability. You can still ask a question.</p>}
      {l.status === "sold" && <p className="text-sm text-white bg-slate-900 rounded-xl p-3">This item has sold.</p>}
      {l.status !== "sold" && (
        <button data-testid="action-ask-question" className={btnOutline + " w-full h-12"} onClick={need(() => (v.conversation_id ? nav(`/inbox/${v.conversation_id}`) : setDlg("question")))}>
          <MessageCircle className="h-4 w-4" /> {v.conversation_id ? "Open conversation" : "Ask a question"}
        </button>
      )}
      <OfferDialog open={dlg === "offer"} onOpenChange={(o) => setDlg(o ? "offer" : null)} listing={l} />
      <PickupDialog open={dlg === "pickup"} onOpenChange={(o) => setDlg(o ? "pickup" : null)} listing={l} offer={acceptedOffer} />
      <QuestionDialog open={dlg === "question"} onOpenChange={(o) => setDlg(o ? "question" : null)} listing={l} />
    </div>
  );
};

export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [l, setL] = useState(null);
  const [seller, setSeller] = useState(null);
  const [err, setErr] = useState("");
  const [report, setReport] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/listings/${id}`);
      setL(data);
      api.get(`/users/${data.seller_id}/profile`).then((r) => setSeller(r.data)).catch(() => {});
    } catch (e) {
      setErr(errMsg(e));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleSave = async () => {
    if (!user) return nav("/login", { state: { from: `/listing/${id}` } });
    try {
      if (l.viewer.saved) await api.delete(`/listings/${id}/save`);
      else await api.post(`/listings/${id}/save`);
      setL({ ...l, viewer: { ...l.viewer, saved: !l.viewer.saved } });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (err) return <ErrorState message={err} onRetry={load} />;
  if (!l) return <Loading />;
  return (
    <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 lg:gap-10">
      <Gallery photos={l.photos} title={l.title} />
      <div className="space-y-5">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <StatusPill status={l.status} />
            {l.is_demo && <span data-testid="demo-listing-badge" className="text-[11px] font-bold uppercase px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600">Demo listing · fictional</span>}
          </div>
          <h1 data-testid="listing-title" className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{l.title}</h1>
          <div className="flex items-center gap-3 mt-2">
            <span data-testid="listing-price" className="text-3xl font-extrabold text-slate-900 font-mono">{money(l.price)}</span>
            <span className="text-xs font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-700">{l.price_type === "firm" ? "Firm price" : "Offers accepted"}</span>
            <button data-testid="listing-save-btn" onClick={toggleSave} aria-label="Save" className="ml-auto h-10 w-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50">
              <Heart className={`h-5 w-5 ${l.viewer.saved ? "fill-blue-600 text-blue-600" : ""}`} />
            </button>
          </div>
          <p className="text-sm text-slate-500 mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {l.area}{l.area !== l.city ? `, ${l.city}` : ""} (approximate)</span>
            <span>{CATEGORY_LABELS[l.category]} · {CONDITION_LABELS[l.condition]}</span>
            <span>Listed {timeAgo(l.created_at)}</span>
          </p>
          {l.vehicle && <p data-testid="listing-vehicle-info" className="mt-2 text-sm font-semibold text-slate-800">{vehicleLine(l.vehicle)}</p>}
          {l.vehicle?.test_drive && <span data-testid="listing-testdrive-tag" className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1"><Car className="h-4 w-4" /> Test drive welcome: ask for one when you book a pickup</span>}
        </div>
        {l.viewer.is_seller ? <SellerTools l={l} reload={load} /> : l.demo_locked ? (
          <p data-testid="demo-locked-notice" className="text-sm text-slate-700 bg-slate-100 border border-slate-200 rounded-xl p-3">This is a fictional demo listing. It's kept separate from real activity, so offers, pickups and questions are turned off.</p>
        ) : <BuyerActions l={l} reload={load} />}
        <div className={card + " space-y-4"}>
          <p data-testid="listing-description" className="text-slate-800 whitespace-pre-line text-sm leading-relaxed">{l.description}</p>
          {l.pickup_windows?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Seller's pickup windows</p>
              <div className="flex flex-wrap gap-2">{l.pickup_windows.map((w) => <span key={w} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-blue-50 text-blue-800">{w}</span>)}</div>
            </div>
          )}
          {l.transport_notes && <p className="text-sm text-slate-700 flex gap-2"><Truck className="h-4 w-4 mt-0.5 text-slate-500 shrink-0" /> {l.transport_notes}</p>}
        </div>
        {seller && (
          <Link to={`/u/${l.seller_id}`} data-testid="seller-card" className={card + " block hover:border-blue-300 transition-colors"}>
            <div className="flex items-center gap-3 mb-3">
              <Avatar user={seller.user} size={44} />
              <div>
                <p className="font-bold text-slate-900">{seller.user.first_name}</p>
                <p className="text-xs text-slate-500">{seller.user.city} · Joined {fmtDate(seller.user.created_at)}</p>
              </div>
            </div>
            <TrustSummary profile={seller} compact />
          </Link>
        )}
        {!l.viewer.is_seller && user && (
          <button data-testid="report-listing-btn" onClick={() => setReport(true)} className={btnGhost + " text-slate-500"}><Flag className="h-4 w-4" /> Report listing</button>
        )}
        <ReportDialog open={report} onOpenChange={setReport} targetType="listing" targetId={l.id} />
      </div>
    </div>
  );
}
